import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, ShieldCheck, Star, Clock } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ASSETS } from '../mock/data';

const Login = ({ cleanerMode = false }) => {
  const nav = useNavigate();
  const { login } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogle = () => {
    const redirectUrl = window.location.origin + (cleanerMode ? '/cleaner/dashboard' : '/home');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true); setErr('');
    try {
      const u = await login({ email, password });
      nav(u.role === 'cleaner' ? '/cleaner/dashboard' : (u.role === 'admin' ? '/admin' : (u.role === 'franchise_admin' ? '/franchise' : (u.role === 'chief_accountant' ? '/ho' : (u.role === 'staff' ? '/staff' : '/home')))));
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const google = () => startGoogle();

  return (
    <div className="min-h-screen bg-white flex flex-col md:flex-row">
      {/* LEFT VISUAL PANEL (desktop only) */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFF7C2 0%, #FFEB99 100%)' }}>
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#FFE066] opacity-70" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#F5E663] opacity-60" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <button onClick={() => nav('/')} className="self-start">
            <img src={ASSETS.logo} alt="Clean24" className="h-16" />
          </button>

          <div>
            <img src={cleanerMode ? ASSETS.staff : ASSETS.waterTank} alt="" className="rounded-2xl shadow-xl h-72 w-full object-cover" />
            <h2 className="mt-6 text-3xl font-extrabold text-neutral-900">
              {cleanerMode ? 'Grow with Clean24' : 'Sparkling clean, on demand.'}
            </h2>
            <p className="mt-2 text-neutral-700">
              {cleanerMode ? 'Get consistent jobs and timely payouts. Flexible hours — be your own boss.' : 'Book verified cleaners in Thrissur & Ernakulam. Pay cash or UPI. Live tracking included.'}
            </p>
            <div className="mt-4 flex items-center gap-4 text-xs text-neutral-700">
              <div className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[#2E7D32]" /> 100% guarantee</div>
              <div className="flex items-center gap-1"><Clock className="w-4 h-4 text-[#2E7D32]" /> 30-min express</div>
              <div className="flex items-center gap-1"><Star className="w-4 h-4 text-[#F5B301] fill-[#F5B301]" /> 4.9 rated</div>
            </div>
          </div>

          <p className="text-xs text-neutral-600">© 2026 Clean24. All rights reserved.</p>
        </div>
      </div>

      {/* RIGHT FORM */}
      <div className="flex-1 flex items-center justify-center px-5 md:px-8 py-8">
        <div className="w-full max-w-md">
          <button onClick={() => nav(-1)} className="md:hidden w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
            <ArrowLeft className="w-4 h-4" />
          </button>

          <div className="md:hidden flex flex-col items-center mb-6">
            <img src={ASSETS.logo} alt="Clean24" className="h-16" />
          </div>

          <h1 className="text-2xl md:text-3xl font-extrabold text-neutral-900">{cleanerMode ? 'Cleaner Login' : 'Customer Login'}</h1>
          <p className="text-sm text-neutral-600 mt-1">{cleanerMode ? 'Login to receive nearby jobs' : 'Sign in as customer'}</p>

          {err && <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{err}</div>}

          <form onSubmit={submit} className="mt-6 space-y-3">
            <div className="relative">
              <Mail className="w-4 h-4 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 bg-white focus:outline-none focus:border-[#5AA02C] focus:ring-2 focus:ring-[#5AA02C]/20 text-sm" />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input type={show ? 'text' : 'password'} required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-11 py-3 rounded-xl border border-neutral-200 bg-white focus:outline-none focus:border-[#5AA02C] focus:ring-2 focus:ring-[#5AA02C]/20 text-sm" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="flex justify-end">
              <button type="button" className="text-xs text-[#5AA02C] font-semibold hover:underline">Forgot password?</button>
            </div>

            <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-70 text-white font-semibold transition-colors">
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-neutral-200 flex-1" />
            <span className="text-xs text-neutral-400">OR</span>
            <div className="h-px bg-neutral-200 flex-1" />
          </div>

          <button onClick={google} className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-neutral-200 bg-white hover:bg-neutral-50 transition-colors font-medium text-sm">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <p className="mt-6 text-center text-sm text-neutral-600">
            Don't have an account? <Link to={cleanerMode ? '/cleaner/signup' : '/signup'} className="text-[#5AA02C] font-semibold">Sign up</Link>
          </p>

          {!cleanerMode && (
            <p className="mt-2 text-center text-xs text-neutral-500">
              Are you a cleaner? <Link to="/cleaner/login" className="text-[#2E7D32] font-semibold">Cleaner login</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
